import { CrewMember, PlanningWeekStats, Scalars, WorkPackage, WorkPackageFilterInput } from '@bechtel/types';
import { filter, includes } from 'lodash';
import React, { createContext, useContext, useReducer } from 'react';
import { MockAllForeman, MockAllGeneralForeman, MockAllSuperintendent } from './form-data';

export enum PlanningWorkPackageSlider {
  Single,
  Multiple,
  All,
}

interface PlanningContextProps {
  selectedWpList?: WorkPackage['id'][];
  totalSelectedCount?: number;
  isReplan?: Scalars['Boolean'];
  unitOfMeasure?: Scalars['String'];
  selectedForeman?: CrewMember;
  selectedGeneralForeman?: CrewMember;
  selectedSuperintendent?: CrewMember;
  weekStartDate?: PlanningWeekStats['startDate'];
  weekEndDate?: PlanningWeekStats['endDate'];
  resetWorkPackagePager?: boolean;
  planningWorkPackageSlider?: PlanningWorkPackageSlider;
  workPackageFilterInput?: WorkPackageFilterInput;
  aiPlannedMilestoneCount?: number;
}

export enum PlanningActionType {
  SingleWorkPackageSelected = 'SingleWorkPackageSelected',
  MultiWorkPackageSelected = 'MultiWorkPackageSelected',
  AllWorkPackageSelected = 'AllWorkPackageSelected',
  ReplanMilestone = 'ReplanMilestone',
  CrewMemberSelected = 'CrewMemberSelected',
  ResetWorkPackageSelection = 'ResetWorkPackageSelection',
  SetUnitOfMeasure = 'SetUnitOfMeasure',
  SetPlanningWeekSchedule = 'SetPlanningWeekSchedule',
  SetPlanningWorkPackageSlider = 'SetPlanningWorkPackageSlider',
  SetWorkPackageFilterInput = 'SetWorkPackageFilterInput',
  SetAiPlannedMilestoneCount = 'SetAiPlannedMilestoneCount',
}

interface PlanningDispatchContextProps {
  type: PlanningActionType;
  payload: PlanningContextProps;
}

const defaultValue = () => null;
export const PlanningDispatchContext = createContext<(arg: PlanningDispatchContextProps) => void>(defaultValue);

const planningContextInitialState: PlanningContextProps = {
  selectedWpList: [],
  isReplan: false,
  unitOfMeasure: 'hours',
  selectedForeman: MockAllForeman,
  selectedGeneralForeman: MockAllGeneralForeman,
  selectedSuperintendent: MockAllSuperintendent,
  weekStartDate: undefined,
  weekEndDate: undefined,
  resetWorkPackagePager: false,
};

export const PlanningDataContext = createContext<PlanningContextProps>(planningContextInitialState);

const usePlanning = () => {
  const planningContext = useContext(PlanningDataContext);
  const planningDispatch = useContext(PlanningDispatchContext);
  return { ...planningContext, planningDispatch };
};

export function planningReducer(state: PlanningContextProps, action: PlanningDispatchContextProps) {
  const { type, payload } = action;
  const {
    isReplan,
    selectedWpList,
    totalSelectedCount,
    unitOfMeasure,
    selectedForeman,
    selectedGeneralForeman,
    selectedSuperintendent,
    weekStartDate,
    weekEndDate,
    resetWorkPackagePager,
    workPackageFilterInput,
  } = payload;
  switch (type) {
    case PlanningActionType.SingleWorkPackageSelected: {
      return { ...state, selectedWpList: selectedWpList };
    }
    case PlanningActionType.MultiWorkPackageSelected: {
      const wpList = state.selectedWpList;
      const isWpAdded = selectedWpList && selectedWpList.length > 0 && includes(wpList, selectedWpList[0]);
      if (isWpAdded) {
        const wpIds = filter(wpList, (id) => {
          return id != selectedWpList[0];
        });
        return { ...state, selectedWpList: wpIds };
      } else {
        return { ...state, selectedWpList: [...wpList, ...selectedWpList] };
      }
    }
    case PlanningActionType.AllWorkPackageSelected: {
      return { ...state, selectedWpList: selectedWpList, totalSelectedCount: totalSelectedCount };
    }
    case PlanningActionType.ResetWorkPackageSelection: {
      return {
        ...state,
        selectedWpList: selectedWpList,
        resetWorkPackagePager: resetWorkPackagePager,
      };
    }
    case PlanningActionType.CrewMemberSelected: {
      const {
        selectedSuperintendent: supCrewMember,
        selectedGeneralForeman: gfCrewMember,
        selectedForeman: fmCrewMember,
      } = state;
      return {
        ...state,
        isReplan,
        selectedForeman: selectedForeman ? selectedForeman : fmCrewMember,
        selectedGeneralForeman: selectedGeneralForeman ? selectedGeneralForeman : gfCrewMember,
        selectedSuperintendent: selectedSuperintendent ? selectedSuperintendent : supCrewMember,
      };
    }
    case PlanningActionType.ReplanMilestone: {
      return { ...state, isReplan };
    }
    case PlanningActionType.SetUnitOfMeasure: {
      return { ...state, unitOfMeasure: unitOfMeasure };
    }
    case PlanningActionType.SetPlanningWeekSchedule: {
      return { ...state, weekStartDate, weekEndDate };
    }
    case PlanningActionType.SetPlanningWorkPackageSlider: {
      return { ...state, planningWorkPackageSlider: payload.planningWorkPackageSlider };
    }
    case PlanningActionType.SetWorkPackageFilterInput: {
      return { ...state, workPackageFilterInput };
    }
    case PlanningActionType.SetAiPlannedMilestoneCount: {
      return { ...state, aiPlannedMilestoneCount: payload.aiPlannedMilestoneCount };
    }
    default:
      return state;
  }
}

const PlanningContext = ({ children }: React.ReactNode) => {
  const [state, dispatch] = useReducer(planningReducer, planningContextInitialState);
  return (
    <PlanningDispatchContext.Provider value={dispatch}>
      <PlanningDataContext.Provider value={state}>{children}</PlanningDataContext.Provider>
    </PlanningDispatchContext.Provider>
  );
};

export { PlanningContext, usePlanning };
